import { isAmazonMusicAddress } from './amazonMusicUrl';
import { smartLinkPlatformDomains } from './domains';

/**
 * The outside links of a SmartLink version (Music24 admin review queue, spec
 * docs/superpowers/specs/2026-10-10-music24-admin-rehaul-design.md section 5).
 *
 * An outside link is a link that leaves the set of hosts we trust: the music services, our own
 * domains, the big social networks and a few named music sites. A page with no outside link needs
 * no review. An email address is always an outside link.
 *
 * Pure code: no react-intl, no Node module. music24-api (the publish rule, the review queue) and
 * the admin SPA read it.
 */

export type OutsideLinkSource = 'button' | 'social' | 'text' | 'email';

export interface OutsideLink {
  /** The address as the page has it. An email address has no `mailto:` prefix. */
  url: string;
  /** The host in lower case with no `www.`. The mail domain for an email. Empty when the address has none. */
  host: string;
  /** The button text, when the link is a button with one. */
  label: string | null;
  /** `button`: a link, player or media block. `social`: the social links block. `text`: found in text. */
  source: OutsideLinkSource;
}

/** What `findOutsideLinks` reads from a widget: the stored row or the serialized widget. */
export interface OutsideLinkWidget {
  type: string;
  data: unknown;
}

export interface OutsideLinkContext {
  /**
   * The links that the social links block shows. The block stores nothing: the server reads them
   * from the channels and the service connections of the artist. They count only when the version
   * has a `socialLinks` block.
   */
  socialLinks?: readonly { url: string }[];
  /** More safe hosts of this page, for example the custom domain of the artist. */
  extraSafeHosts?: readonly string[];
  /**
   * More hosts that the embed address of a player can have, for example the MusicAPI host of a
   * local environment. They are safe only for `player.embedUrl`, not for a button.
   */
  extraEmbedHosts?: readonly string[];
}

/** The hosts of the 22 music services. The service metadata of fym-shared holds no host. */
export const MUSIC_SERVICE_HOSTS: readonly string[] = [
  // Amazon Music: not `amazon.com`, which is a shop. `amazon.<tld>/music/...` is Amazon Music too:
  // `isAmazonMusicAddress` reads the path.
  'music.amazon.com',
  'music.amazon.co.uk',
  'music.amazon.de',
  'music.amazon.fr',
  'music.amazon.it',
  'music.amazon.es',
  'music.amazon.co.jp',
  'music.amazon.in',
  'music.amazon.ca',
  'music.amazon.com.au',
  'music.amazon.com.br',
  'music.amazon.com.mx',
  'anghami.com',
  'apple.com',
  'deezer.com',
  'pandora.com',
  'soundcloud.com',
  'spotify.com',
  'spotify.link',
  'tidal.com',
  'youtube.com',
  'youtu.be',
  'napster.com',
  'qobuz.com',
  'y.qq.com',
  'music.qq.com',
  'music.yandex.ru',
  'music.yandex.com',
  'vk.com',
  'zvuk.com',
  'gaana.com',
  'jiosaavn.com',
  'saavn.com',
  'resso.com',
  'boomplay.com',
  'audiomack.com',
  'audius.co',
];

/** Our own hosts: the SmartLink platform domains and the Music24 domains (production and staging). */
export const MUSIC24_HOSTS: readonly string[] = [
  ...smartLinkPlatformDomains,
  'music24.com',
  'musicpawa.com',
  'pawamusic.com',
];

/** The big social networks and the named music sites of the spec. */
export const SOCIAL_AND_MUSIC_SITE_HOSTS: readonly string[] = [
  'instagram.com',
  'tiktok.com',
  'facebook.com',
  'fb.com',
  'x.com',
  'twitter.com',
  'threads.net',
  'threads.com',
  'twitch.tv',
  'discord.com',
  'discord.gg',
  'bandcamp.com',
  'beatport.com',
  'songkick.com',
  'bandsintown.com',
  'genius.com',
  'shazam.com',
];

/** Every safe host. A subdomain of a safe host is safe. */
export const SAFE_LINK_HOSTS: readonly string[] = [
  ...MUSIC_SERVICE_HOSTS,
  ...MUSIC24_HOSTS,
  ...SOCIAL_AND_MUSIC_SITE_HOSTS,
];

/**
 * Link shorteners hide the target, so they are never safe, also when a later change adds a parent
 * domain to the safe list. `linktr.ee` is not a shortener; it is not in the safe list, so it is an
 * outside link too.
 */
export const LINK_SHORTENER_HOSTS: readonly string[] = [
  'bit.ly',
  'bitly.com',
  'tinyurl.com',
  't.co',
  'goo.gl',
  'ow.ly',
  'is.gd',
  'buff.ly',
  'rebrand.ly',
  'cutt.ly',
  'shorturl.at',
  'tiny.cc',
  'rb.gy',
  't.ly',
  'fb.me',
  'lnkd.in',
  's.id',
  'v.gd',
  'shorte.st',
  'adf.ly',
];

/**
 * The redirect endpoints of safe hosts. Such an address sends the visitor to any site, so it is
 * never safe. An entry with no path is a host that only redirects.
 */
export const REDIRECT_ENDPOINTS: readonly { host: string; path?: string }[] = [
  { host: 'vk.com', path: '/away.php' },
  { host: 'youtube.com', path: '/redirect' },
  { host: 'l.facebook.com' },
  { host: 'lm.facebook.com' },
  { host: 'l.instagram.com' },
  { host: 'tiktok.com', path: '/link' },
  { host: 'google.com', path: '/url' },
  { host: 't.umblr.com' },
  { host: 'linkedin.com', path: '/redir' },
  { host: 'href.li' },
];

/**
 * The hosts of the player embed address (`player.embedUrl`) that are not a music service: the
 * MusicAPI embed page (`public/embed/...`, production and staging). The other embed addresses that
 * the server makes are on a music service host (`open.spotify.com/embed`, `embed.tidal.com`,
 * `www.youtube.com/embed`, `w.soundcloud.com`, `embed.music.apple.com`), which is safe already.
 */
export const PLAYER_EMBED_HOSTS: readonly string[] = ['api.musicapi.com', 'api.musicmigrator.com'];

/** The text scan reads at most this many characters of one text. A longer text gets one entry for a person. */
export const TEXT_SCAN_LIMIT = 5000;

/** The address of the entry that says a text was too long to scan in full. It is not a link. */
export const LONG_TEXT_MARK = 'long text';

/**
 * The top-level domains that make a bare word a domain ("mysite.com"). The list is short on
 * purpose: "feat.someone" and "Mr.Smith" in a text must not become links.
 */
// prettier-ignore
const BARE_DOMAIN_TLDS: readonly string[] = [
  'com', 'net', 'org', 'io', 'co', 'me', 'ly', 'fm', 'link', 'gg', 'tv', 'app', 'xyz', 'info', 'biz', 'to', 'uk', 'de',
  'fr', 'es', 'it', 'nl', 'pl', 'ru', 'br', 'in', 'jp', 'cn', 'au', 'ca', 'us', 'eu', 'ch', 'se', 'no', 'dk', 'fi',
  'be', 'at', 'cz', 'pt', 'gr', 'tr', 'mx', 'ar', 'kr', 'id', 'ph', 'ng', 'za', 'club', 'site', 'online', 'store',
  'shop', 'live', 'music', 'page', 'dev', 'ai', 'top', 'win', 'vip', 'cc', 'ws', 'lol', 'pro', 'art', 'band', 'bio',
  'ee', 'icu', 'click', 'cfd', 'tk', 'bet', 'casino',
];

// Each pattern has bounded repeats, and the text scan reads at most TEXT_SCAN_LIMIT characters. An
// open repeat in front of a literal (`[a-z0-9._%+-]+@`) makes the scan quadratic: 100,000
// characters with no `@` took 12 seconds.
/** The local part is 64 characters at most, a domain label 63 (RFC 5321). */
const EMAIL_PATTERN = /[a-z0-9._%+-]{1,64}@[a-z0-9-]{1,63}(?:\.[a-z0-9-]{1,63}){1,8}/gi;
const SCHEME_URL_PATTERN = /\bhttps?:\/\/[^\s<>"'`]+/gi;
/** Group 1: the character before the domain. Group 2: the domain and its path. Group 3: the top-level domain. */
const BARE_DOMAIN_PATTERN =
  /(^|[^@\w.-])((?:[a-z0-9][a-z0-9-]{0,62}\.){1,8}([a-z]{2,10})(?![a-z0-9-])(?:\/[^\s<>"'`]*)?)/gi;
/** Punctuation that ends a sentence, not an address. */
const TRAILING_PUNCTUATION = '.,;:!?)]}>';

/** Cuts the punctuation at the end. A loop, not a `+$` pattern: that pattern is quadratic on a long run. */
function stripTrailingPunctuation(value: string): string {
  let end = value.length;
  while (end > 0 && TRAILING_PUNCTUATION.includes(value.charAt(end - 1))) {
    end -= 1;
  }
  return value.slice(0, end);
}

/** The host in lower case with no `www.` and no trailing dot. */
export function normalizeLinkHost(host: string): string {
  return host
    .trim()
    .toLowerCase()
    .replace(/\.$/, '')
    .replace(/^www\./, '');
}

const matchesHost = (host: string, domain: string): boolean => host === domain || host.endsWith(`.${domain}`);

/** True for a link shortener, and for a subdomain of one. */
export function isLinkShortenerHost(host: string): boolean {
  const normal = normalizeLinkHost(host);
  return LINK_SHORTENER_HOSTS.some((domain) => matchesHost(normal, domain));
}

/** True when a link to this host needs no review. A shortener is never safe. */
export function isSafeLinkHost(host: string, extraSafeHosts: readonly string[] = []): boolean {
  const normal = normalizeLinkHost(host);
  if (normal === '' || isLinkShortenerHost(normal)) {
    return false;
  }
  return [...SAFE_LINK_HOSTS, ...extraSafeHosts.map(normalizeLinkHost)].some(
    (domain) => domain !== '' && matchesHost(normal, domain)
  );
}

/** True for a redirect endpoint of `REDIRECT_ENDPOINTS`: the host (or a subdomain) and the start of the path. */
export function isRedirectEndpoint(host: string, path: string): boolean {
  const normal = normalizeLinkHost(host);
  const lowerPath = path.toLowerCase();
  return REDIRECT_ENDPOINTS.some(
    (endpoint) =>
      matchesHost(normal, endpoint.host) &&
      (endpoint.path === undefined || lowerPath === endpoint.path || lowerPath.startsWith(`${endpoint.path}/`))
  );
}

interface ParsedAddress {
  kind: 'web' | 'email' | 'other';
  /** The address to show. */
  url: string;
  host: string;
  /** The path of a web address, for the redirect endpoints. */
  path: string;
}

/** Reads one address of a button. An address with no scheme is read as `https://`. */
function parseAddress(raw: string): ParsedAddress | null {
  const value = raw.trim();
  if (value === '') {
    return null;
  }
  if (/^mailto:/i.test(value)) {
    const address = value.replace(/^mailto:/i, '').split('?')[0] ?? '';
    return { kind: 'email', url: address, host: normalizeLinkHost(address.split('@')[1] ?? ''), path: '' };
  }
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(withScheme);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      // `tel:`, `sms:`, `javascript:` and the like: never a safe web link.
      return { kind: 'other', url: value, host: '', path: '' };
    }
    return { kind: 'web', url: value, host: normalizeLinkHost(url.hostname), path: url.pathname };
  } catch {
    return { kind: 'other', url: value, host: '', path: '' };
  }
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const textOf = (value: unknown): string | null => (typeof value === 'string' && value.trim() !== '' ? value : null);

function collectStrings(value: unknown, out: string[] = []): string[] {
  if (typeof value === 'string') {
    out.push(value);
  } else if (Array.isArray(value)) {
    value.forEach((item) => collectStrings(item, out));
  } else if (isRecord(value)) {
    Object.values(value).forEach((item) => collectStrings(item, out));
  }
  return out;
}

/**
 * Finds the outside links of the widgets of one SmartLink version.
 *
 * It reads:
 * - `link`: the address and the button text. A block that points at an own page keeps its stored
 *   address too: the public page falls back to it when the target page is gone;
 * - `player`: the address of the player and the embed address that the page puts in an iframe;
 * - `media`: the "Tap opens" address and the caption;
 * - `socialLinks`: the links of `context.socialLinks`, or of a serialized widget;
 * - `info` and `profile`: each URL, bare domain and email address in the text.
 *
 * A redirect endpoint of a safe host (`REDIRECT_ENDPOINTS`) is an outside link. A text longer than
 * `TEXT_SCAN_LIMIT` is scanned up to the limit and gets one entry (`LONG_TEXT_MARK`), so a person
 * looks at the page.
 *
 * The result has one entry per address and source, in the order of the widgets.
 */
export function findOutsideLinks(
  widgets: readonly OutsideLinkWidget[],
  context: OutsideLinkContext = {}
): OutsideLink[] {
  const extraSafeHosts = context.extraSafeHosts ?? [];
  const found: OutsideLink[] = [];
  const seen = new Set<string>();

  const add = (link: OutsideLink) => {
    const key = `${link.source}|${link.url.toLowerCase()}`;
    if (!seen.has(key)) {
      seen.add(key);
      found.push(link);
    }
  };

  const addAddress = (
    raw: string,
    label: string | null,
    source: 'button' | 'social' | 'text',
    safeHosts: readonly string[] = extraSafeHosts
  ) => {
    const parsed = parseAddress(raw);
    if (parsed === null) {
      return;
    }
    if (parsed.kind === 'email') {
      add({ url: parsed.url, host: parsed.host, label, source: 'email' });
      return;
    }
    if (
      parsed.kind === 'web' &&
      ((isSafeLinkHost(parsed.host, safeHosts) && !isRedirectEndpoint(parsed.host, parsed.path)) ||
        isAmazonMusicAddress(parsed.host, parsed.path))
    ) {
      return;
    }
    add({ url: parsed.url, host: parsed.host, label, source });
  };

  const addText = (fullText: string) => {
    const text = fullText.length > TEXT_SCAN_LIMIT ? fullText.slice(0, TEXT_SCAN_LIMIT) : fullText;
    if (fullText.length > TEXT_SCAN_LIMIT) {
      add({
        url: LONG_TEXT_MARK,
        host: '',
        label: `text longer than ${TEXT_SCAN_LIMIT} characters: only the start was checked`,
        source: 'text',
      });
    }
    let rest = text;
    for (const match of text.match(EMAIL_PATTERN) ?? []) {
      const address = stripTrailingPunctuation(match);
      add({ url: address, host: normalizeLinkHost(address.split('@')[1] ?? ''), label: null, source: 'email' });
    }
    rest = rest.replace(EMAIL_PATTERN, ' ');
    for (const match of rest.match(SCHEME_URL_PATTERN) ?? []) {
      addAddress(stripTrailingPunctuation(match), null, 'text');
    }
    rest = rest.replace(SCHEME_URL_PATTERN, ' ');
    const bare = new RegExp(BARE_DOMAIN_PATTERN.source, 'gi');
    let match = bare.exec(rest);
    while (match !== null) {
      const tld = (match[3] ?? '').toLowerCase();
      if (BARE_DOMAIN_TLDS.includes(tld)) {
        addAddress(stripTrailingPunctuation(match[2] ?? ''), null, 'text');
      }
      match = bare.exec(rest);
    }
  };

  for (const widget of widgets) {
    const data = isRecord(widget.data) ? widget.data : {};
    switch (widget.type) {
      case 'link': {
        const label = textOf(data.label);
        if (label !== null) {
          addText(label);
        }
        // A block that points at an own page (`targetSmartLinkId`) keeps its stored address. The
        // public page falls back to it when the target page is gone, so it is always read.
        const url = textOf(data.url);
        if (url !== null) {
          addAddress(url, label, 'button');
        }
        break;
      }
      case 'player': {
        const url = textOf(data.url);
        if (url !== null) {
          addAddress(url, null, 'button');
        }
        // The public page puts the embed address in an iframe. The update body is free JSON, so an
        // artist can store any address there.
        const embedUrl = textOf(data.embedUrl);
        if (embedUrl !== null) {
          addAddress(embedUrl, 'Player embed', 'button', [
            ...extraSafeHosts,
            ...PLAYER_EMBED_HOSTS,
            ...(context.extraEmbedHosts ?? []),
          ]);
        }
        break;
      }
      case 'media': {
        const linkUrl = textOf(data.linkUrl);
        if (linkUrl !== null) {
          addAddress(linkUrl, null, 'button');
        }
        const caption = textOf(data.caption);
        if (caption !== null) {
          addText(caption);
        }
        break;
      }
      case 'socialLinks': {
        const serialized = Array.isArray(data.socialLinks) ? data.socialLinks : [];
        for (const link of [...serialized, ...(context.socialLinks ?? [])]) {
          const url = isRecord(link) ? textOf(link.url) : null;
          if (url !== null) {
            addAddress(url, null, 'social');
          }
        }
        break;
      }
      case 'info':
      case 'profile': {
        collectStrings(data.text === undefined ? data : data.text).forEach(addText);
        break;
      }
      default:
        break;
    }
  }
  return found;
}
